import React, { useState, useEffect } from 'react';
import { MessageSquare, Save, X as XIcon, AlertTriangle, X, CheckCircle, Copy, ChevronDown, ChevronUp, KeyRound } from 'lucide-react';
import {
  Box,
  CircularProgress,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import MainService from '../../services/MainService';
import SmsKeywordTextField, { SMS_KEYWORDS } from '../Messages/SmsKeywordTextField';

const SMSConfiguration = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [keywordModalOpen, setKeywordModalOpen] = useState(false);

  useEffect(() => {
    loadMessages();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      setError('');
      
      const requestData = {
        Skip: 0,
        Take: 1000
      };

      const response = await MainService.MessagesInGrid(requestData);
      
      if (response && response.Data) {
        setMessages(response.Data || []);
      } else if (Array.isArray(response)) {
        setMessages(response);
      } else {
        setMessages([]);
      }
      setHasChanges(false);
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Failed to load SMS messages');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (index, field, value) => {
    setMessages(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return updated;
    });
    setHasChanges(true);
  };

  const handleUpdate = async () => {
    try {
      setSaving(true);
      setError('');
      
      const response = await MainService.UpdateMessageList(messages);
      
      if (response === true || response === 'Success') {
        setSuccess('SMS messages updated successfully');
        setHasChanges(false);
        loadMessages();
      } else {
        setError(response || 'Failed to update SMS messages');
      }
    } catch (err) {
      console.error('Error updating messages:', err);
      setError('Failed to update SMS messages');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    loadMessages();
  };

  const toggleCard = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess(`Copied "${text}" to clipboard`);
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center">
            <MessageSquare className="w-6 h-6 text-[#46955A] mr-2" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">SMS Configuration</h1>
              <p className="text-sm text-gray-500 mt-1">Manage SMS message templates and settings</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outlined"
              startIcon={<KeyRound className="w-4 h-4" />}
              onClick={() => setKeywordModalOpen(true)}
              sx={{
                borderColor: '#46955A',
                color: '#46955A',
                '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
              }}
            >
              Key References
            </Button>
            {hasChanges && (
              <Button
                variant="outlined"
                startIcon={<XIcon className="w-4 h-4" />}
                onClick={handleCancel}
                disabled={saving}
                sx={{
                  borderColor: '#6B7280',
                  color: '#6B7280',
                  '&:hover': { borderColor: '#4B5563', backgroundColor: '#F9FAFB' }
                }}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<Save className="w-4 h-4" />}
              onClick={handleUpdate}
              disabled={saving || !hasChanges}
              sx={{
                backgroundColor: '#46955A',
                '&:hover': { backgroundColor: '#3d7d4f' },
                '&:disabled': { backgroundColor: '#9CA3AF' }
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Messages Cards */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={32} sx={{ color: '#46955A' }} />
              <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Loading...</span>
            </Box>
          </Box>
        ) : messages.length > 0 ? (
          <div className="space-y-4">
            {messages.map((message, index) => (
              <div
                key={message.Id || index}
                className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Card Header */}
                <div
                  className="p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleCard(index)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        {expandedCards[index] ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                        <span className="px-3 py-1 bg-[#f0f9f4] text-[#46955A] rounded text-sm font-medium border border-[#46955A]/20">
                          {message.Type || 'N/A'}
                        </span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm font-medium">
                          {message.Keyword || 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={message.IsEnable || false}
                            onChange={(e) => handleFieldChange(index, 'IsEnable', e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              color: '#46955A',
                              '&.Mui-checked': { color: '#46955A' }
                            }}
                          />
                        }
                        label="Enable"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <Collapse in={expandedCards[index]}>
                  <div className="p-6 space-y-6">
                    {/* Message Templates */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Client Messages */}
                      <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Client Messages</h3>
                        <SmsKeywordTextField
                          fullWidth
                          label="Client Message (English)"
                          multiline
                          rows={3}
                          value={message.ClientMsgEN || ''}
                          onValueChange={(value) => handleFieldChange(index, 'ClientMsgEN', value)}
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#46955A',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#46955A',
                              },
                            },
                          }}
                        />
                        <SmsKeywordTextField
                          fullWidth
                          label="Client Message (Arabic)"
                          multiline
                          rows={3}
                          value={message.ClientMsgAR || ''}
                          onValueChange={(value) => handleFieldChange(index, 'ClientMsgAR', value)}
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#46955A',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#46955A',
                              },
                            },
                          }}
                        />
                      </div>

                      {/* Customer Messages */}
                      <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Customer Messages</h3>
                        <SmsKeywordTextField
                          fullWidth
                          label="Customer Message (English)"
                          multiline
                          rows={3}
                          value={message.CustomerMsgEN || ''}
                          onValueChange={(value) => handleFieldChange(index, 'CustomerMsgEN', value)}
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#46955A',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#46955A',
                              },
                            },
                          }}
                        />
                        <SmsKeywordTextField
                          fullWidth
                          label="Customer Message (Arabic)"
                          multiline
                          rows={3}
                          value={message.CustomerMsgAR || ''}
                          onValueChange={(value) => handleFieldChange(index, 'CustomerMsgAR', value)}
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#46955A',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#46955A',
                              },
                            },
                          }}
                        />
                      </div>
                    </div>

                    {/* Settings Row */}
                    <div className="flex items-center gap-6 pt-4 border-t border-gray-200">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={message.IsClient || false}
                            onChange={(e) => handleFieldChange(index, 'IsClient', e.target.checked)}
                            sx={{
                              color: '#46955A',
                              '&.Mui-checked': { color: '#46955A' }
                            }}
                          />
                        }
                        label="Is Client"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={message.IsCustomer || false}
                            onChange={(e) => handleFieldChange(index, 'IsCustomer', e.target.checked)}
                            sx={{
                              color: '#46955A',
                              '&.Mui-checked': { color: '#46955A' }
                            }}
                          />
                        }
                        label="Is Customer"
                      />
                      <TextField
                        label="SMS Type"
                        value={message.SMSType || ''}
                        onChange={(e) => handleFieldChange(index, 'SMSType', e.target.value)}
                        size="small"
                        sx={{
                          width: '200px',
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: '#46955A',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#46955A',
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                </Collapse>
              </div>
            ))}
          </div>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 8 }}>
            <MessageSquare className="w-8 h-8 text-gray-400" />
            <Box sx={{ color: '#6B7280' }}>No SMS messages available</Box>
          </Box>
        )}
      </div>

      <Dialog open={keywordModalOpen} onClose={() => setKeywordModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#111827', fontWeight: 700 }}>
          Keyword References
        </DialogTitle>
        <DialogContent dividers>
          <p className="text-sm text-gray-500 mb-4">Click on any keyword to copy it. You can also type @ inside a message to insert keywords directly.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SMS_KEYWORDS.map((keyword, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-[#46955A] hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-700">{keyword.name}</div>
                  <div className="text-xs text-gray-500 font-mono mt-1">{keyword.keyword}</div>
                </div>
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(keyword.keyword)}
                    sx={{
                      color: '#46955A',
                      '&:hover': { backgroundColor: '#f0f9f4' }
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </IconButton>
                </Tooltip>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeywordModalOpen(false)} sx={{ color: '#46955A' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SMSConfiguration;
